import { useState, useCallback } from 'react';
import { useAccount, useSignMessage } from 'wagmi';
import { walletApi } from '../api/wallet';

interface LinkResult {
  linking: boolean;
  error: string | null;
  link: () => Promise<boolean>;
}

/**
 * SIWE-style wallet linking:
 *   1. Request a challenge from backend (server returns a message containing a nonce)
 *   2. User signs the message with their connected wallet
 *   3. POST signature + message to backend to link wallet to current authenticated user
 */
export function useLinkWallet(): LinkResult {
  const { address } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const link = useCallback(async () => {
    if (!address) {
      setError('Connect a wallet first');
      return false;
    }
    setLinking(true);
    setError(null);
    try {
      const challenge = await walletApi.challenge(address);
      const signature = await signMessageAsync({ message: challenge.message });
      await walletApi.link({ message: challenge.message, signature });
      return true;
    } catch (err: unknown) {
      const e = err as { shortMessage?: string; message?: string };
      setError(e.shortMessage ?? e.message ?? 'Linking failed');
      return false;
    } finally {
      setLinking(false);
    }
  }, [address, signMessageAsync]);

  return { linking, error, link };
}
